import { useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MainNav } from "@/components/MainNav";
import { Separator } from "@/components/ui/separator";
import { useBlogPosts } from "@/hooks/blog";
import { useHydratedPostBySlug } from "@/hooks/blog/useHydratedPostBySlug";
import { isSSR } from "@/lib/isSSR";
import { useToast } from "@/components/ui/use-toast";
import { BlogPostHeader } from "@/components/blog/BlogPostHeader";
import { BlogPostMetadata } from "@/components/blog/BlogPostMetadata";
import { BlogPostFeaturedImage } from "@/components/blog/BlogPostFeaturedImage";
import { BlogPostContent } from "@/components/blog/BlogPostContent";
import { ServerBlogPostContent } from "@/components/blog/ServerBlogPostContent";
import { BlogPostTags } from "@/components/blog/BlogPostTags";
import { BlogPostShareButton } from "@/components/blog/BlogPostShareButton";
import { RelatedPosts } from "@/components/blog/RelatedPosts";
import { BlogPostSEO } from "@/components/blog/BlogPostSEO";
import { BlogPostLoadingState } from "@/components/blog/BlogPostLoadingState";
import { BlogPostNotFound } from "@/components/blog/BlogPostNotFound";
import { PageSEO } from "@/components/PageSEO";

// Get blog post data from global window object
const getPreloadedBlogPost = (slug: string) => {
  if (typeof window === 'undefined') {
    return null;
  }
  
  try {
    if (window.__BLOG_POST_DATA__ && window.__BLOG_POST_DATA__[slug]) {
      // Ensure the cached data matches the current slug to prevent wrong content
      const cachedPost = window.__BLOG_POST_DATA__[slug];
      if (cachedPost && cachedPost.slug === slug) {
        return cachedPost;
      }
    }
    
    // Try to find data in script tag
    const scriptEl = document.getElementById('__BLOG_POST_DATA__');
    if (scriptEl && scriptEl.textContent) {
      const data = JSON.parse(scriptEl.textContent);
      if (data && data[slug] && data[slug].slug === slug) {
        return data[slug];
      }
    }
  } catch (e) {
    console.error('Error parsing blog post data:', e);
  }
  
  return null;
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || "";
  const isServerSide = isSSR();
  
  // Keep track of the previous slug to detect changes
  const prevSlugRef = useRef<string | null>(null);
  const hasSlugChanged = prevSlugRef.current !== null && prevSlugRef.current !== currentSlug;
  
  // Get preloaded data from SSR
  const preloadedPost = currentSlug ? getPreloadedBlogPost(currentSlug) : null;
  
  // Use preloaded post data if available, otherwise load from API
  const { post, loading, error } = useHydratedPostBySlug(currentSlug, preloadedPost);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Prioritize preloaded data from SSR, then fetched data
  const displayPost = preloadedPost || post;
  
  // Determine if we have data available (either preloaded or fetched)
  const hasPostData = Boolean(displayPost);
  const isDataLoading = loading && !preloadedPost;
  
  const postCategory = useMemo(() => {
    if (displayPost?.categories && displayPost.categories.length > 0) {
      return displayPost.categories[0];
    }
    return "";
  }, [displayPost]);
  
  const { 
    posts: relatedPosts,
    loading: relatedLoading 
  } = useBlogPosts({
    initialPerPage: 3,
    ...(postCategory ? { category: postCategory } : {})
  });
  
  const filteredRelatedPosts = useMemo(() => {
    if (!displayPost) return [];
    return relatedPosts
      .filter(p => p.id !== displayPost.id)
      .slice(0, 3);
  }, [relatedPosts, displayPost]);
  
  // Handle slug changes and clear cache when navigating between related posts
  useEffect(() => {
    if (hasSlugChanged) {
      console.log(`[BlogPost] Slug changed from ${prevSlugRef.current} to ${currentSlug}, clearing old cache`);
      
      // Clear window cache for the old slug to prevent conflicts
      if (!isServerSide && typeof window !== 'undefined' && window.__BLOG_POST_DATA__ && prevSlugRef.current) {
        delete window.__BLOG_POST_DATA__[prevSlugRef.current];
        console.log(`[BlogPost] Cleared window cache for old slug: ${prevSlugRef.current}`);
      }
    }
    
    // Update the previous slug reference
    prevSlugRef.current = currentSlug;
  }, [currentSlug, hasSlugChanged, isServerSide]);
  
  useEffect(() => {
    if (error) {
      toast({
        title: "Errore",
        description: "Impossibile caricare il post. Riprova più tardi.",
        variant: "destructive"
      });
      
      const timer = setTimeout(() => {
        navigate("/blog");
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [error, navigate, toast]);
  
  // Store blog post data for client-side hydration (only on client)
  // Only store if the post matches the current slug to prevent cache pollution
  useEffect(() => {
    if (displayPost?.title && displayPost?.slug === currentSlug && typeof window !== 'undefined' && currentSlug && !isServerSide) {
      // Store post data for SSR hydration
      if (typeof window.__BLOG_POST_DATA__ === 'undefined') {
        window.__BLOG_POST_DATA__ = {};
      }
      
      if (window.__BLOG_POST_DATA__ && currentSlug) {
        window.__BLOG_POST_DATA__[currentSlug] = displayPost;
        console.log(`[BlogPost] Cached post data for slug: ${currentSlug}`);
      }
    }
  }, [displayPost, currentSlug, isServerSide]);
  
  // Scroll to top when slug changes (for better UX when navigating between related posts)
  useEffect(() => {
    if (hasSlugChanged) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [hasSlugChanged]);
  
  // Render loading state (only if we don't have preloaded data and we're loading)
  if (isDataLoading) {
    return (
      <>
        <PageSEO
          title="Caricamento articolo... - Blog Gildy"
          description="Stiamo caricando il contenuto richiesto, attendi qualche istante."
          canonicalPath={`/blog/${currentSlug || ''}`}
          ssrMode={isServerSide}
        />
        <BlogPostLoadingState />
      </>
    );
  }
  
  // Render not found state
  if (!hasPostData && !isDataLoading) {
    return (
      <>
        <PageSEO
          title="Articolo non trovato - Blog Gildy"
          description="L'articolo che stai cercando non esiste o è stato rimosso."
          canonicalPath={`/blog/${currentSlug || ''}`}
          noIndex={true}
          ssrMode={isServerSide}
        />
        <BlogPostNotFound />
      </>
    );
  }

  // Main blog post render
  return (
    <div className="min-h-screen flex flex-col">
      {/* BlogPostSEO generates meta tags for both SSR and client-side rendering */}
      {displayPost && (
        <BlogPostSEO 
          post={displayPost} 
          ssrMode={isServerSide} 
        />
      )}

      <MainNav />
      <main className="container mx-auto py-6 px-4 md:px-6">
        {displayPost && (
          <>
            <BlogPostHeader 
              title={displayPost.title} 
              categories={displayPost.categories || []} 
            />
            
            <BlogPostMetadata 
              author={displayPost.author || {name: ""}}
              date={displayPost.date || ""}
              readingTime={displayPost.readingTime || ""}
            />
            
            <BlogPostFeaturedImage 
              src={displayPost.featuredImage || ""} 
              alt={displayPost.featuredImageAlt || displayPost.title || ""}
            />
            
            {displayPost.content && (
              isServerSide ? (
                <ServerBlogPostContent content={displayPost.content} />
              ) : (
                <BlogPostContent content={displayPost.content} />
              )
            )}
            
            <BlogPostTags tags={displayPost.tags || []} />
            
            <BlogPostShareButton title={displayPost.title} />
            
            <Separator className="mb-10" />
          </>
        )}
        
        <RelatedPosts 
          posts={filteredRelatedPosts} 
          currentPostId={displayPost?.id}
          loading={relatedLoading} 
        />
      </main>
    </div>
  );
};

export default BlogPost;
